<template>
  <div id="choiceEdit">
    <div class="container">

      <ChoiceNavigation></ChoiceNavigation>

      <div class="page-title">
        <span class="name">{{ $to(data.name) }}</span>
        <button class="save btn btn-primary" @click="save" v-if="$store.getters.loggedUserCanModifyInventory">{{
          $t("save") }}</button>
      </div>

      <Tabs>

        <TabSection :title="$t('choices.title')" v-if="data">
          <div class="columns">
            <div class="column">
              <div class="group">
                <LocalizedInput :title="$t('choices.name')" :translate="data.name" />
              </div>
            </div>

            <div class="column">
              <div class="group">
                <LocalizedInput :title="$t('choices.placeholder')" :translate="data.placeholder" />
              </div>
            </div>
          </div>

          <div class="group">
            <div class="subtitle">{{ $t('choices.combo_products') }}</div>
            <ListMultiSelect :bank="choiceItems" :selected="data.slaveItems"
              :bank-title="$t('choices.combo_product_bank')" :selected-title="$t('choices.selected_combo_product')">
            </ListMultiSelect>
          </div>

          <div class="subtitle">{{ $t('choices.default_value') }}</div>
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('choices.optional') }}</label>
                <select v-model="data.optional">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{ $t('choices.default_product') }}</label>
                <select v-model="data.default">
                  <option :value="null" class="null-choice">{{ $t('choices.none') }}</option>
                  <option v-for="selection of data.slaveItems" :value="selection.id" :key="selection.id">
                    {{ $to(selection.name) }}</option>
                </select>
              </div>
            </div>
          </div>

          <Panel :title="$t('items.modifier_group_bank')" collapsible collapsed>
            <ListMultiSelect :bank="modifierGroups" :selected="data.modifierGroups"
              :bank-title="$t('items.modifier_group_bank')" :selected-title="$t('items.selected_modifier_groups')">
            </ListMultiSelect>
          </Panel>

          <Panel :title="$t('choices.assign_to_group')" v-show="false" collapsible collapsed>
            <ListMultiSelect :bank="choiceGroups" :selected="data.choiceGroups"
              :bank-title="$t('choices.combo_group_bank')" :selected-title="$t('choices.selected_combo_group')">
            </ListMultiSelect>
          </Panel>

          <Panel :title="$t('choices.assign_to_product')" v-show="false" collapsible collapsed>
            <ListMultiSelect :bank="items" :selected="data.masterItems" :bank-title="$t('choices.product_bank')"
              :selected-title="$t('choices.selected_product')"></ListMultiSelect>
          </Panel>

        </TabSection>

        <TabSection :title="$t('choices.availability_per_service')">
          <div>{{ $t('choices.availability_per_service_description') }}</div>
          <br>
          <div class="subtitle">{{ $t('choices.product_availability') }}</div>
          <div class="group" v-show="hasDeliveryMethod('takeout')">
            <input id="chkTakeoutAvailable" type="checkbox" v-model="data.availabilityPerOrderMethod.takeout">
            <label for="chkTakeoutAvailable">{{ $t("deliveryMethod.takeout") }}</label>
          </div>
          <div class="group" v-show="hasDeliveryMethod('delivery')">
            <input id="chkDeliveryAvailable" type="checkbox" v-model="data.availabilityPerOrderMethod.delivery">
            <label for="chkDeliveryAvailable">{{ $t("deliveryMethod.delivery") }}</label>
          </div>
          <div class="group" v-show="hasDeliveryMethod('inPlace')">
            <input id="chkInPlaceAvailable" type="checkbox" v-model="data.availabilityPerOrderMethod.inPlace">
            <label for="chkInPlaceAvailable">{{ $t("deliveryMethod.inPlace") }}</label>
          </div>
          <div class="group" v-show="hasDeliveryMethod('hotel')">
            <input id="chkHotelAvailable" type="checkbox" v-model="data.availabilityPerOrderMethod.hotel">
            <label for="chkHotelAvailable">{{ $t("deliveryMethod.hotel") }}</label>
          </div>
          <div class="group" v-show="hasDeliveryMethod('catering')">
            <input id="chkCateringAvailable" type="checkbox" v-model="data.availabilityPerOrderMethod.catering">
            <label for="chkCateringAvailable">{{ $t("deliveryMethod.catering") }}</label>
          </div>
        </TabSection>

        <InventoryUsagePanel v-if="$route.query.id" type="choice" :id="$route.query.id"></InventoryUsagePanel>

      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserCanModifyInventory">
        <button class="btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import ChoiceNavigation from '@/components/choices/Navigation.vue';
  import ListMultiSelect from "@/components/tools/ListMultiSelect.vue";
  import { mapActions } from "vuex";
  import LocalizedInput from "@/components/tools/LocalizedInput";

  export default {
    name: "choiceEdit",

    components: { ChoiceNavigation, ListMultiSelect, LocalizedInput },

    data() {
      return {
        inventory: null,
        choiceItems: [{
          id: null,
          name: { fr: "" }
        }],
        items: [{
          id: null,
          name: { fr: "" }
        }],
        choiceGroups: [{
          id: null,
          name: { fr: "" }
        }],
        modifierGroups: [{
          id: null,
          name: { fr: "" }
        }],
        data: {
          id: null,
          name: { fr: "" },
          placeholder: { fr: "" },
          optional: false,
          default: null,
          masterItems: [],
          slaveItems: [],
          choiceGroups: [],
          modifierGroups: [],
          availabilityPerOrderMethod: {
            takeout: true,
            hotel: true,
            catering: true,
            delivery: true,
            inPlace: true
          }
        }
      }
    },

    computed: {
      inventoryReference() {
        return this.$store.state.contextCompany ? this.$store.state.contextCompany.nameCanonical : this.inventory.id;
      },
      deliveryMethods() {
        return this.inventory ? this.inventory.deliveryMethods : [];
      }
    },

    validate: {
      "data.name.fr": {
        required: false
      }
    },

    async mounted() {
      let choiceId = this.$route.query.id;
      this.inventory = await this.$store.getters.getContextInventory;

      this.$store.commit('showSpinner', true);
      Promise
        .all([
          choiceId ? this.getChoice({ inventoryId: this.inventoryReference, id: choiceId }) : Promise.resolve(),
          this.getChoiceGroups({ inventoryId: this.inventoryReference }),
          this.getChoiceItems({ inventoryId: this.inventoryReference }),
          this.getItems({ inventoryId: this.inventoryReference }),
          this.getModifierGroups({ inventoryId: this.inventoryReference }),
        ])
        .then(([choice, choiceGroups, choiceItems, items, modifierGroups]) => {
          if (choice && choiceId) {
            this.data.id = choiceId;
            this.data.name = choice.name;
            this.data.placeholder = choice.placeholder;
            this.data.optional = choice.optional;
            this.data.default = choice.default;
            this.data.masterItems = choice.masterItems;
            this.data.slaveItems = choice.slaveItems;
            this.data.choiceGroups = choice.choiceGroups;
            this.data.modifierGroups = choice.modifierGroups;
            this.data.availabilityPerOrderMethod = choice.availabilityPerOrderMethod;
          }
          if (Array.isArray(this.data.name) && this.data.name.length === 0) { //Fix in case of invalid choice name value from importation data
            this.data.name = { fr: "", en: "" };
          }
          this.choiceGroups = choiceGroups
            .map(c => ({
              id: c.id,
              name: c.name,
              note: c.note
            }));
          this.choiceItems = choiceItems
            .map(c => ({
              id: c.id,
              name: c.name,
              note: c.note,
              choices: c.choices
            }));
          this.items = items
            .map(c => ({
              id: c.id,
              name: c.name,
              note: c.note
            }));
          this.modifierGroups = modifierGroups
            .map(c => ({
              id: c.id,
              name: c.name,
              note: c.note
            }));
          this._formatChoiceGroups();
          this._formatSlaveItems();
          this._formatMasterItems();
          this._formatModifierGroups();

          if (!this.data.availabilityPerOrderMethod) {
            this.data.availabilityPerOrderMethod = {
              takeout: true,
              hotel: true,
              catering: true,
              delivery: true,
              inPlace: true
            }
          }
        })
        .catch(e => {
          console.log(e)
        })
        .finally(() => {
          if (this.$store.getters.isBranchContextInventory) {
            window.lockFields(this.$store.getters.getOverloadableFields);
          } else if (!this.$store.getters.loggedUserCanModifyInventory) {
            window.lockFields();
          }
          this.$store.commit('showSpinner', false);
        });
    },

    methods: {

      ...mapActions(["getChoice", "createChoice", "saveChoice", "getItems", "getChoiceGroups", "getChoiceItems", "getModifierGroups"]),

      hasDeliveryMethod(method) {
        return this.deliveryMethods.indexOf(method) > -1;
      },

      async save() {

        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }

        this.$store.commit("showSpinner", true);
        let data = this._formatBeforeSave();

        let firstInvalid = this._findFirstInvalidChoiceItem(data.slaveItems);
        if (firstInvalid) {
          this.$store.commit("showSpinner", false);
          toast(this.$t("modifiers.error.invalid_combo_product_relationship") +
            "<br><b>" + this.$to(firstInvalid) + "</b>", "error");
          return;
        }

        if (this.data.id) {
          let response = await this.saveChoice({ inventoryId: this.inventoryReference, itemId: this.data.id, data: data });
          if (response) {
            toast(this.$t("choice.success"), "success");
          } else {
            toast(this.$t("modifiers.error.save"), "error");
            return;
          }
        } else {
          let response = await this.createChoice({ inventoryId: this.inventoryReference, data: data });
          if (response) {
            this.data.id = response.id;
            toast(this.$t("choice.success"), "success");
            //this.$router.push(this.$router.buildContext("/choices/edit", {id: response.id}));
          } else {
            toast(this.$t("modifiers.error.save"), "error");
            return;
          }
        }
        this.$router.push(this.$router.buildContext("/choices"));
        this.$store.commit("showSpinner", false);

      },

      _formatSlaveItems() {
        let array = [];
        for (let i = 0; i < this.data.slaveItems.length; i++) {
          let found_item = this.choiceItems.find((x) => { return x.id === this.data.slaveItems[i] });
          array.push(found_item);
        }
        this.data.slaveItems = array;
      },
      _formatChoiceGroups() {
        let array = [];
        for (let i = 0; i < this.data.choiceGroups.length; i++) {
          let found_item = this.choiceGroups.find((x) => { return x.id === this.data.choiceGroups[i] });
          if (found_item) {
            array.push(found_item);
          }
        }
        this.data.choiceGroups = array;
      },
      _formatMasterItems() {
        let array = [];
        for (let i = 0; i < this.data.masterItems.length; i++) {
          let found_item = this.items.find((x) => { return x.id === this.data.masterItems[i] });
          if (found_item) {
            array.push(found_item);
          }
        }
        this.data.masterItems = array;
      },
      _formatModifierGroups() {
        let array = [];
        for (let i = 0; i < this.data.modifierGroups.length; i++) {
          let found_item = this.modifierGroups.find((x) => { return x.id === this.data.modifierGroups[i] });
          if (found_item) {
            array.push(found_item);
          }
        }
        this.data.modifierGroups = array;
      },
      _findFirstInvalidChoiceItem(choiceItems) {
        let choiceId = this.$route.query.id;
        for (let item of choiceItems) {
          let itemDetail = this.choiceItems.find(c => c.id === item);
          if (itemDetail && itemDetail.choices.find(i => i == choiceId)) {
            return itemDetail.name;
          }
        }
      },
      _formatBeforeSave() {
        let data = JSON.parse(JSON.stringify(this.data));

        let slaveItems = data.slaveItems.map((x) => { return x.id });
        let masterItems = data.masterItems.map((x) => { return x.id });
        let choiceGroups = data.choiceGroups.map((x) => { return x.id });
        let modifierGroups = data.modifierGroups.map((x) => { return x.id });

        data.slaveItems = slaveItems;
        data.masterItems = masterItems;
        data.choiceGroups = choiceGroups;
        data.modifierGroups = modifierGroups;

        return data;
      },
    }
  }
</script>

<style lang="scss" scoped>
  .subtitle {
    padding-top: 10px;
  }

  .title {
    .name {
      padding-right: 20px;
    }
  }

  .null-choice {
    font-style: italic;
  }
</style>

<style lang="scss">
  #choiceEdit {
    display: flex;

    .panel .panel {
      padding: 20px 0 0 0;

      &>.title {
        text-transform: uppercase;
        font-weight: bold;
        font-size: 14px;
        color: var(--primary-color);
      }
    }
  }
</style>